import { useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router'
import { useTranslation } from 'react-i18next'
import {
  Edit,
  Trash2,
  CheckCircle,
  Calendar,
  Building2,
  Tag,
  User,
  CreditCard,
  FileText,
  Wrench,
  FileSignature,
  Receipt,
  Percent,
  BanknoteArrowDown,
  FileDown,
  FilePen,
} from 'lucide-react'
import { PageHeader, Breadcrumbs } from '@/components/layout'
import { Button, Card, Badge, EmptyState, Skeleton, Modal } from '@/components/ui'
import {
  useExpense,
  useDeleteExpense,
  useReimburseExpense,
  useCompleteExpense,
  useExpenseBankTransaction,
} from '@/hooks/useExpenses'
import { useAuth } from '@/hooks/useAuth'
import { useDateFormatter } from '@/hooks/useDateFormatter'
import { formatBaht } from '@/lib/money'
import { useDocumentTemplates } from '@/hooks/useDocumentTemplates'
import { useProperty } from '@/hooks/useProperties'
import { useVendorDetail } from '@/hooks/useCmms'
import { useAccountSettings } from '@/hooks/useAccountSettings'
import { useNotificationStore } from '@/stores/useNotificationStore'

const API_URL = import.meta.env.VITE_API_URL || ''

export function ExpenseDetailPage() {
  const { t, i18n } = useTranslation('expenses')
  const { t: tc } = useTranslation('common')
  const { id } = useParams<{ id: string }>()
  const expenseId = Number(id)
  const navigate = useNavigate()
  const { user } = useAuth()
  const formatDate = useDateFormatter()

  const { data: expense, isLoading, isFetching } = useExpense(expenseId)
  const { data: matchedBankTxn, isLoading: isLoadingReconciliation } =
    useExpenseBankTransaction(expenseId)
  const deleteExpense = useDeleteExpense()
  const reimburseExpense = useReimburseExpense()
  const completeExpense = useCompleteExpense()

  const { data: accountSettings } = useAccountSettings()
  const { data: whtTemplates } = useDocumentTemplates(1, 'wht_50_tawi')
  const { data: reimbursementTemplates } = useDocumentTemplates(1, 'expense_reimbursement_receipt')
  const { data: property } = useProperty(expense?.property_id ?? 0)
  const { data: vendorDetail } = useVendorDetail(expense?.vendor_id ?? 0)
  const addToast = useNotificationStore((s) => s.addToast)

  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [showReimburseModal, setShowReimburseModal] = useState(false)
  const [showCompleteModal, setShowCompleteModal] = useState(false)
  const [isGeneratingWht, setIsGeneratingWht] = useState(false)
  const [isGeneratingReceipt, setIsGeneratingReceipt] = useState(false)

  const isAdminOrOwner = user?.role === 'admin' || user?.role === 'owner'
  // T129-011 / D-6: accountant can generate pdfme documents (50 ทวิ + receipt)
  // but NOT approve Reimburse/Complete (Q-3 separation of duty — stays isAdminOrOwner)
  const isAccountant = user?.role === 'accountant'

  const handleDelete = async () => {
    await deleteExpense.mutateAsync(expenseId)
    navigate('/expenses')
  }

  const handleReimburse = async () => {
    await reimburseExpense.mutateAsync(expenseId)
    setShowReimburseModal(false)
  }

  const handleComplete = async () => {
    await completeExpense.mutateAsync(expenseId)
    setShowCompleteModal(false)
  }

  const handleGenerateWht = async () => {
    if (!expense || !property) return
    const tmpl = whtTemplates?.data?.[0]?.template as import('@pdfme/common').Template | undefined
    if (!tmpl) {
      addToast({ type: 'error', message: t('messages.no_wht_template') })
      return
    }
    setIsGeneratingWht(true)
    try {
      const { downloadWhtCertificatePdf } = await import('@/lib/whtCertificatePdfGenerator')
      await downloadWhtCertificatePdf(expense, vendorDetail ?? null, property, tmpl, accountSettings)
    } finally {
      setIsGeneratingWht(false)
    }
  }

  const handleDownloadReimbursementReceipt = async () => {
    if (!expense || !property) return
    const tmpl = reimbursementTemplates?.data?.[0]?.template as import('@pdfme/common').Template | undefined
    if (!tmpl) {
      addToast({ type: 'error', message: t('messages.no_reimbursement_template') })
      return
    }
    setIsGeneratingReceipt(true)
    try {
      const { downloadExpenseReceiptPdf } = await import('@/lib/expenseReceiptPdfGenerator')
      await downloadExpenseReceiptPdf(expense, property, tmpl, accountSettings)
    } finally {
      setIsGeneratingReceipt(false)
    }
  }

  const getStatusBadge = () => {
    if (!expense) return null
    switch (expense.status) {
      case 'completed':
        return <Badge variant="success">{t('statuses.completed')}</Badge>
      case 'pending_reimbursement_approval':
        return <Badge variant="warning">{t('statuses.pending_reimbursement_approval')}</Badge>
      case 'reimbursement_approved':
        return <Badge variant="info">{t('statuses.reimbursement_approved')}</Badge>
      // T156-019 (EP-156 §5 decision 15): every new non-reimbursement expense
      // starts here and waits for owner/admin confirmation.
      case 'pending_confirmation':
        return <Badge variant="warning">{t('statuses.pending_confirmation')}</Badge>
      default:
        return <Badge variant="ghost">{expense.status}</Badge>
    }
  }

  if (isLoading) {
    return (
        <div className="space-y-6">
          <Skeleton className="h-8 w-64" />
          <Card>
            <Skeleton className="h-48" />
          </Card>
        </div>
    )
  }

  if (!expense) {
    return (
        <Card>
          <EmptyState
            title={tc('errors.not_found')}
            action={<Button onClick={() => navigate('/expenses')}>{tc('actions.back')}</Button>}
          />
        </Card>
    )
  }

  const breadcrumbLabel = expense.description?.trim() || expense.vendor_name_resolved || expense.vendor_name || formatDate(expense.expense_date)

  // Derive month for reconciliation deep-link
  const expenseMonth = expense.expense_date.substring(0, 7)

  return (
    <>
      <div className="space-y-6">
        <Breadcrumbs
          items={[
            { label: t('title'), href: '/expenses' },
            { label: breadcrumbLabel },
          ]}
        />

        <PageHeader
          title={breadcrumbLabel}
          description={t('labels.date') + ': ' + formatDate(expense.expense_date)}
          isRefreshing={isFetching && !isLoading}
          // EP-106 Item 1 — primary is the state-driven approval step
          // (Reimburse while pending, Complete once approved); the two are
          // mutually exclusive so exactly one primary (or none) is ever live.
          primaryAction={
            expense.status === 'pending_reimbursement_approval' && isAdminOrOwner
              ? {
                  key: 'reimburse',
                  label: t('actions.reimburse'),
                  icon: CheckCircle,
                  onClick: () => setShowReimburseModal(true),
                  loading: reimburseExpense.isPending,
                }
              : expense.status === 'reimbursement_approved' && isAdminOrOwner
                ? {
                    key: 'complete',
                    label: t('actions.mark_complete'),
                    icon: CheckCircle,
                    onClick: () => setShowCompleteModal(true),
                    loading: completeExpense.isPending,
                  }
                // T156-019 (EP-156 §5 decision 15): every other new expense
                // waits the same way, confirmed through the same endpoint.
                : expense.status === 'pending_confirmation' && isAdminOrOwner
                  ? {
                      key: 'confirm_expense',
                      label: t('actions.confirm_expense'),
                      icon: CheckCircle,
                      onClick: () => setShowCompleteModal(true),
                      loading: completeExpense.isPending,
                    }
                  : undefined
          }
          overflowActions={[
            {
              key: 'generate_wht',
              label: t('actions.generate_wht_certificate'),
              icon: FilePen,
              onClick: handleGenerateWht,
              loading: isGeneratingWht,
              hidden: !((isAdminOrOwner || isAccountant) && (expense?.wht_rate ?? 0) > 0),
            },
            {
              key: 'download_receipt',
              label: t('actions.download_reimbursement_receipt'),
              icon: FileDown,
              onClick: handleDownloadReimbursementReceipt,
              loading: isGeneratingReceipt,
              hidden: !((isAdminOrOwner || isAccountant) && expense?.status === 'reimbursement_approved'),
            },
            {
              key: 'edit',
              label: tc('actions.edit'),
              icon: Edit,
              to: `/expenses/${id}/edit`,
            },
            {
              key: 'delete',
              label: tc('actions.delete'),
              icon: Trash2,
              onClick: () => setShowDeleteModal(true),
              destructive: true,
              // EP-129: destroy is in the accountant's manage set (doc D-2) —
              // the BE ExpensePolicy#destroy? was widened; keep FE in sync.
              hidden: !(isAdminOrOwner || isAccountant),
            },
          ]}
        />

        <div className="grid gap-6 lg:grid-cols-3">
          {/* Main Info */}
          <Card className="lg:col-span-2 space-y-0">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold">{t('labels.category')}</h2>
              {getStatusBadge()}
            </div>

            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
              <div className="min-w-0">
                <dt className="text-sm text-base-content/60 flex items-center gap-1">
                  <Calendar className="w-4 h-4" /> {t('labels.date')}
                </dt>
                <dd className="font-medium">{formatDate(expense.expense_date)}</dd>
              </div>
              <div className="min-w-0">
                <dt className="text-sm text-base-content/60 flex items-center gap-1">
                  <Building2 className="w-4 h-4" /> {t('labels.property')}
                </dt>
                <dd className="font-medium break-words">{expense.property_name}</dd>
              </div>
              <div className="min-w-0">
                <dt className="text-sm text-base-content/60 flex items-center gap-1">
                  <Tag className="w-4 h-4" /> {t('labels.category')}
                </dt>
                <dd className="font-medium">
                  {/* expense.category is operator-configurable (accountSettings.settings.expense_categories),
                      not a fixed backend enum — branch on i18n.exists() so custom categories render as-is. */}
                  {i18n.exists(`expenses:categories.${expense.category}`)
                    ? t(`categories.${expense.category}`)
                    : expense.category}
                </dd>
              </div>
              <div className="min-w-0">
                <dt className="text-sm text-base-content/60 flex items-center gap-1">
                  <CreditCard className="w-4 h-4" /> {t('labels.payment_method')}
                </dt>
                <dd className="font-medium">
                  {expense.payment_method
                    ? t(`payment_methods.${expense.payment_method}`)
                    : '-'}
                </dd>
              </div>
              <div className="min-w-0">
                <dt className="text-sm text-base-content/60 flex items-center gap-1">
                  <User className="w-4 h-4" /> {t('labels.paid_by')}
                </dt>
                <dd className="font-medium break-words">{expense.paid_by ?? '-'}</dd>
              </div>
              <div className="min-w-0">
                <dt className="text-sm text-base-content/60 flex items-center gap-1">
                  <User className="w-4 h-4" /> {t('labels.created_by')}
                </dt>
                <dd className="font-medium break-words">{expense.created_by_name}</dd>
              </div>
              {expense.maintenance_request_id && (
                <div className="min-w-0">
                  <dt className="text-sm text-base-content/60 flex items-center gap-1">
                    <Wrench className="w-4 h-4" /> {t('labels.maintenance_request')}
                  </dt>
                  <dd className="font-medium">
                    <Link
                      to={`/maintenances/${expense.maintenance_request_id}`}
                      className="link link-primary"
                    >
                      #{expense.maintenance_request_id}
                    </Link>
                  </dd>
                </div>
              )}
              {expense.lease && (
                <div className="min-w-0">
                  <dt className="text-sm text-base-content/60 flex items-center gap-1">
                    <FileSignature className="w-4 h-4" /> {t('labels.lease')}
                  </dt>
                  <dd className="font-medium">
                    <Link to={`/leases/${expense.lease.id}`} className="link link-primary">
                      {expense.lease.lease_code}
                      {expense.lease.unit_number ? ` · ${t('labels.unit')} ${expense.lease.unit_number}` : ''}
                    </Link>
                  </dd>
                </div>
              )}
            </dl>

            {/* Vendor Block */}
            {(expense.vendor_id || expense.vendor_name) && (
              <div className="border-t border-base-200 pt-4 mb-4">
                <h3 className="font-medium mb-3 flex items-center gap-1">
                  <User className="w-4 h-4" /> {t('labels.vendor')}
                </h3>
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="min-w-0">
                    <dt className="text-sm text-base-content/60">{t('labels.vendor_name')}</dt>
                    <dd className="font-medium break-words">
                      {expense.vendor_id ? (
                        <Link to={`/vendors/${expense.vendor_id}`} className="link link-primary">
                          {expense.vendor_name_resolved ?? expense.vendor_name}
                        </Link>
                      ) : (
                        expense.vendor_name
                      )}
                    </dd>
                  </div>
                </dl>
              </div>
            )}

            {/* Description */}
            {expense.description && (
              <div className="border-t border-base-200 pt-4 mb-4">
                <h3 className="font-medium mb-2 flex items-center gap-1">
                  <FileText className="w-4 h-4" /> {t('labels.description')}
                </h3>
                <p className="text-base-content/70 whitespace-pre-wrap">{expense.description}</p>
              </div>
            )}

            {/* Notes */}
            {expense.notes && (
              <div className="border-t border-base-200 pt-4">
                <h3 className="font-medium mb-2">{t('labels.notes')}</h3>
                <p className="text-base-content/70 whitespace-pre-wrap">{expense.notes}</p>
              </div>
            )}

            {/* Approval info */}
            {(expense.approved_by_name || expense.reimbursed_at) && (
              <div className="border-t border-base-200 pt-4 mt-4">
                <h3 className="font-medium mb-3">{t('labels.approved_by')}</h3>
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {expense.approved_by_name && (
                    <div className="min-w-0">
                      <dt className="text-sm text-base-content/60">{t('labels.approved_by')}</dt>
                      <dd className="font-medium break-words">{expense.approved_by_name}</dd>
                    </div>
                  )}
                  {expense.reimbursed_at && (
                    <div className="min-w-0">
                      <dt className="text-sm text-base-content/60">{t('labels.reimbursed_at')}</dt>
                      <dd className="font-medium">{formatDate(expense.reimbursed_at)}</dd>
                    </div>
                  )}
                </dl>
              </div>
            )}
          </Card>

          {/* Summary Sidebar */}
          <div className="space-y-6">
            {/* Amounts Card */}
            <Card>
              <h2 className="text-lg font-semibold mb-4">{tc('labels.amount')}</h2>
              <dl className="space-y-3">
                <div className="flex items-center justify-between">
                  <dt className="text-base-content/60">{t('labels.amount')}</dt>
                  <dd className="font-medium">{formatBaht(expense.amount, { locale: 'en', decimals: 2 })}</dd>
                </div>
                <div className="flex items-center justify-between">
                  <dt className="text-base-content/60">{t('labels.vat_amount')}</dt>
                  <dd className="font-medium">{formatBaht(expense.vat_amount, { locale: 'en', decimals: 2 })}</dd>
                </div>
                {expense.transaction_fee > 0 && (
                  <div className="flex items-center justify-between">
                    <dt className="text-base-content/60">{t('labels.transaction_fee')}</dt>
                    <dd className="font-medium">{formatBaht(expense.transaction_fee, { locale: 'en', decimals: 2 })}</dd>
                  </div>
                )}
                <div className="border-t border-base-200 pt-3 flex items-center justify-between">
                  <dt className="font-semibold">{t('labels.total_amount')}</dt>
                  <dd className="font-bold text-lg">{formatBaht(expense.total_amount, { locale: 'en', decimals: 2 })}</dd>
                </div>
              </dl>
            </Card>

            {/* WHT Block */}
            {expense.wht_rate != null && expense.wht_rate > 0 && (
              <Card>
                <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Percent className="w-5 h-5" /> {t('sections.withholding_tax')}
                </h2>
                <dl className="space-y-3">
                  <div className="flex items-center justify-between">
                    <dt className="text-base-content/60">{t('labels.wht_rate')}</dt>
                    <dd className="font-medium">{(expense.wht_rate * 100).toFixed(1)}%</dd>
                  </div>
                  <div className="flex items-center justify-between">
                    <dt className="text-base-content/60">{t('labels.wht_amount')}</dt>
                    <dd className="font-medium">{formatBaht(expense.wht_amount, { locale: 'en', decimals: 2 })}</dd>
                  </div>
                  <div className="border-t border-base-200 pt-3 flex items-center justify-between">
                    <dt className="font-semibold">{t('labels.net_cash_amount')}</dt>
                    <dd className="font-bold text-lg">{formatBaht(expense.net_cash_amount, { locale: 'en', decimals: 2 })}</dd>
                  </div>
                </dl>
              </Card>
            )}

            {/* Reconciliation State */}
            <Card>
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <BanknoteArrowDown className="w-5 h-5" /> {t('sections.reconciliation')}
              </h2>
              {isLoadingReconciliation ? (
                <Skeleton className="h-10" />
              ) : matchedBankTxn ? (
                <div className="space-y-2">
                  <Badge variant="success">{t('labels.reconciled')}</Badge>
                  <dl className="space-y-1 mt-2">
                    <div className="flex items-center justify-between text-sm">
                      <dt className="text-base-content/60">{tc('labels.date')}</dt>
                      <dd className="font-medium">{formatDate(matchedBankTxn.transaction_date)}</dd>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <dt className="text-base-content/60">{tc('labels.amount')}</dt>
                      <dd className="font-medium">{formatBaht(matchedBankTxn.amount, { locale: 'en', decimals: 2 })}</dd>
                    </div>
                  </dl>
                  <Link
                    to={`/bank-reconciliation?month=${expenseMonth}`}
                    className="link link-primary text-sm mt-2 inline-block"
                  >
                    {tc('actions.view')}
                  </Link>
                </div>
              ) : (
                <Badge variant="ghost">{t('labels.not_reconciled')}</Badge>
              )}
            </Card>

            {/* Status Card */}
            <Card
              className={
                expense.status === 'completed'
                  ? 'bg-success/5 border-success'
                  : expense.status === 'reimbursement_approved'
                    ? 'bg-info/5 border-info'
                    : 'bg-warning/5 border-warning'
              }
            >
              <div className="flex items-center gap-3 mb-4">
                <CheckCircle
                  className={`w-8 h-8 ${
                    expense.status === 'completed'
                      ? 'text-success'
                      : expense.status === 'reimbursement_approved'
                        ? 'text-info'
                        : 'text-warning'
                  }`}
                />
                <div>
                  <div className="text-sm text-base-content/60">{t('labels.status')}</div>
                  <div className="text-lg font-bold">
                    {t(`statuses.${expense.status}`)}
                  </div>
                </div>
              </div>
              {expense.status === 'pending_reimbursement_approval' && isAdminOrOwner && (
                <Button className="w-full" onClick={() => setShowReimburseModal(true)}>
                  {t('actions.reimburse')}
                </Button>
              )}
              {expense.status === 'reimbursement_approved' && isAdminOrOwner && (
                <Button className="w-full" onClick={() => setShowCompleteModal(true)}>
                  {t('actions.mark_complete')}
                </Button>
              )}
              {expense.status === 'pending_confirmation' && isAdminOrOwner && (
                <Button className="w-full" onClick={() => setShowCompleteModal(true)}>
                  {t('actions.confirm_expense')}
                </Button>
              )}
            </Card>
          </div>
        </div>

        {/* Evidence Section */}
        <Card>
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Receipt className="w-5 h-5" /> {t('sections.evidence')}
          </h2>

          {/* Receipt / Tax Invoice */}
          <div className="mb-6">
            <h3 className="font-medium mb-3 text-base-content/70">{t('labels.receipt_images')}</h3>
            {expense.receipt_image_urls.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {expense.receipt_image_urls.map((url, index) => (
                  <a
                    key={index}
                    href={`${API_URL}${url}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="aspect-square rounded-lg overflow-hidden border border-base-200 hover:border-primary transition-colors"
                  >
                    <img
                      src={`${API_URL}${url}`}
                      alt={`${t('labels.receipt_images')} ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </a>
                ))}
              </div>
            ) : (
              <p className="text-sm text-base-content/50">{t('messages_detail.no_attachments')}</p>
            )}
          </div>

          {/* Supporting Photos */}
          <div>
            <h3 className="font-medium mb-3 text-base-content/70">{t('labels.evidence_images')}</h3>
            {expense.evidence_image_urls.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {expense.evidence_image_urls.map((url, index) => (
                  <a
                    key={index}
                    href={`${API_URL}${url}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="aspect-square rounded-lg overflow-hidden border border-base-200 hover:border-primary transition-colors"
                  >
                    <img
                      src={`${API_URL}${url}`}
                      alt={`${t('labels.evidence_images')} ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </a>
                ))}
              </div>
            ) : (
              <p className="text-sm text-base-content/50">{t('messages_detail.no_attachments')}</p>
            )}
          </div>
        </Card>
      </div>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title={tc('actions.delete')}
        footer={
          <>
            <Button variant="ghost" onClick={() => setShowDeleteModal(false)}>
              {tc('actions.cancel')}
            </Button>
            <Button
              variant="error"
              onClick={handleDelete}
              loading={deleteExpense.isPending}
            >
              {tc('actions.delete')}
            </Button>
          </>
        }
      >
        <p>{t('messages.delete_confirm')}</p>
      </Modal>

      {/* Reimburse Confirmation Modal */}
      <Modal
        isOpen={showReimburseModal}
        onClose={() => setShowReimburseModal(false)}
        title={t('actions.reimburse')}
        footer={
          <>
            <Button variant="ghost" onClick={() => setShowReimburseModal(false)}>
              {tc('actions.cancel')}
            </Button>
            <Button
              variant="primary"
              onClick={handleReimburse}
              loading={reimburseExpense.isPending}
            >
              {tc('actions.confirm')}
            </Button>
          </>
        }
      >
        <p>{t('messages.reimburse_confirm')}</p>
      </Modal>

      {/* Mark Complete / Confirm Expense Modal — same endpoint (useCompleteExpense)
          for both reimbursement_approved -> completed and, since T156-019
          (EP-156 §5 decision 15), pending_confirmation -> completed. */}
      <Modal
        isOpen={showCompleteModal}
        onClose={() => setShowCompleteModal(false)}
        title={expense.status === 'pending_confirmation' ? t('actions.confirm_expense') : t('actions.mark_complete')}
        footer={
          <>
            <Button variant="ghost" onClick={() => setShowCompleteModal(false)}>
              {tc('actions.cancel')}
            </Button>
            <Button
              variant="primary"
              onClick={handleComplete}
              loading={completeExpense.isPending}
            >
              {tc('actions.confirm')}
            </Button>
          </>
        }
      >
        <p>{expense.status === 'pending_confirmation' ? t('messages.confirm_expense_confirm') : t('messages.complete_confirm')}</p>
      </Modal>
    </>
  )
}
